import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MomentCard from '../components/MomentCard';

const API_URL = 'https://songmoment.onrender.com';

export default function MomentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchMoment(); }, [id]);

  useEffect(() => {
    if (moment) {
      document.title = `${moment.track_name} — EchoMoments`;
      setMetaTag('og:title', `🎵 ${moment.track_name} by ${moment.artist_name}`);
      setMetaTag('og:description', moment.note || `Shared a moment at ${formatTime(moment.timestamp_ms)} — listen on EchoMoments`);
      setMetaTag('og:image', moment.album_image || 'https://songmoment.pages.dev/og-default.png');
      setMetaTag('og:url', `https://songmoment.pages.dev/moment/${moment.id}`);
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', `🎵 ${moment.track_name} by ${moment.artist_name}`);
      setMetaTag('twitter:description', moment.note || `Shared a moment at ${formatTime(moment.timestamp_ms)}`);
      setMetaTag('twitter:image', moment.album_image || 'https://songmoment.pages.dev/og-default.png');
    }
  }, [moment]);

  const setMetaTag = (property, content) => {
    let el = document.querySelector(`meta[property="${property}"]`) ||
             document.querySelector(`meta[name="${property}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(property.startsWith('twitter') ? 'name' : 'property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const fetchMoment = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/moments/${id}`);
      setMoment(res.data);
    } catch (err) {
      setError('Moment not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080608' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,6,8,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(212,83,126,0.2)',
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4537E, #993556)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, color: '#fff',
            }}>♪</div>
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300, fontSize: 20,
              letterSpacing: '0.15em', color: '#f5e6ed',
              textTransform: 'uppercase',
            }}>Echo Moments</span>
          </div>

          {/* Back button */}
          <button onClick={() => navigate('/')} style={{
            fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'rgba(245,230,237,0.45)',
            background: 'rgba(255,255,255,0.04)',
            border: '0.5px solid rgba(255,255,255,0.1)',
            borderRadius: 20, padding: '6px 16px', cursor: 'pointer',
            transition: 'border-color 0.2s, color 0.2s',
          }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.4)'; e.currentTarget.style.color = '#D4537E'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(245,230,237,0.45)'; }}
          >← Feed</button>
        </div>
      </header>

      {/* Content */}
      <main style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'rgba(245,230,237,0.35)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Loading moment...
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.3 }}>♪</div>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 22, fontWeight: 300,
              color: 'rgba(245,230,237,0.65)', marginBottom: 24,
            }}>{error}</p>
            <button onClick={() => navigate('/')} style={{
              fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#f5e6ed', background: 'transparent',
              border: '0.5px solid rgba(212,83,126,0.6)',
              borderRadius: 20, padding: '8px 20px', cursor: 'pointer',
            }}>Go to feed</button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 10, color: 'rgba(212,83,126,0.7)', marginBottom: 20, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              Shared moment
            </p>
            <MomentCard moment={moment} />
            <div style={{ textAlign: 'center', marginTop: 40 }}>
              <p style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 18, fontWeight: 300,
                color: 'rgba(245,230,237,0.45)', marginBottom: 16,
                letterSpacing: '0.04em',
              }}>
                Discover more moments like this
              </p>
              <button onClick={() => navigate('/')} style={{
                fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: '#f5e6ed', background: 'transparent',
                border: '0.5px solid rgba(212,83,126,0.6)',
                borderRadius: 20, padding: '8px 24px', cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s',
              }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,1)'; e.currentTarget.style.color = '#D4537E'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.6)'; e.currentTarget.style.color = '#f5e6ed'; }}
              >
                ♪ Open EchoMoments
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}